import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "icon", "container", "toggle"]
  static values = { id: String, defaultCollapsed: { type: Boolean, default: false } }

  connect() {
    // Apply state immediately without animation
    this.contentTarget.style.transition = "none"
    this.element.style.transition = "none"
    if (this.hasIconTarget) {
      this.iconTarget.style.transition = "none"
    }

    this.applyState(this.isCollapsed, false)

    // Make container visible now that state is applied
    this.element.classList.add("js-ready")

    // Re-enable transitions after a frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.contentTarget.style.transition = ""
        this.element.style.transition = ""
        if (this.hasIconTarget) {
          this.iconTarget.style.transition = ""
        }
      })
    })

    // Bind container click handler
    this.boundContainerClick = this.containerClick.bind(this)
    this.element.addEventListener("click", this.boundContainerClick)
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundContainerClick)
  }

  containerClick(event) {
    // Only expand when collapsed and not clicking the toggle button
    if (this.isCollapsed && !event.target.closest(".collapse-toggle")) {
      this.toggle(event)
    }
  }

  toggle(event) {
    event.preventDefault()
    const newState = !this.isCollapsed
    this.saveState(newState)
    this.applyState(newState)
  }

  applyState(collapsed, animate = true) {
    if (this.hasContentTarget) {
      if (collapsed) {
        // When collapsing with animation, first set explicit height, then animate to 0
        if (animate && this.contentTarget.style.maxHeight === "none") {
          this.contentTarget.style.maxHeight = this.contentTarget.scrollHeight + "px"
          // Force reflow to ensure the browser registers the starting height
          this.contentTarget.offsetHeight
        }
        this.contentTarget.style.maxHeight = "0px"
        this.contentTarget.style.opacity = "0"
        this.contentTarget.style.marginTop = "0"
        this.contentTarget.style.paddingTop = "0"
        this.element.classList.add("collapsed")
      } else {
        this.contentTarget.style.maxHeight = this.contentTarget.scrollHeight + "px"
        this.contentTarget.style.opacity = "1"
        this.contentTarget.style.marginTop = ""
        this.contentTarget.style.paddingTop = ""
        this.element.classList.remove("collapsed")

        // After transition completes, remove maxHeight constraint so content can resize freely
        if (animate) {
          this.contentTarget.addEventListener("transitionend", this.handleTransitionEnd, { once: true })
        } else {
          // No animation, set immediately
          this.contentTarget.style.maxHeight = "none"
        }
      }
    }

    if (this.hasIconTarget) {
      this.iconTarget.style.transform = collapsed ? "rotate(0deg) scale(1.5)" : "rotate(180deg) scale(1.5)"
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-expanded", !collapsed)
    }
  }

  handleTransitionEnd = (event) => {
    // Only handle maxHeight transitions
    if (event.propertyName === "max-height" && !this.isCollapsed) {
      this.contentTarget.style.maxHeight = "none"
    }
  }

  saveState(collapsed) {
    const states = this.getStoredStates()
    states[this.idValue] = collapsed
    localStorage.setItem("collapsedSections", JSON.stringify(states))
  }

  getStoredStates() {
    try {
      return JSON.parse(localStorage.getItem("collapsedSections")) || {}
    } catch {
      return {}
    }
  }

  get isCollapsed() {
    const states = this.getStoredStates()
    if (this.idValue in states) return states[this.idValue] === true
    return this.defaultCollapsedValue
  }
}
